import produce from "immer";
import create from "zustand";
import { ArtistId, PlaylistOrder } from "../../models/Artist";
import { CURRENT_PRIVACY_POLICY_VERSION, OwnerId, PrivacyConsent } from "../../models/User";
import { ArtistService } from "../../services/ArtistService";
import { LoginService } from "../../services/LoginService";
import { ProfileService } from "../../services/ProfileService";
import { SettingsService } from "../../services/SettingsService";

export type ProfileState = {
  darkMode: boolean;
compact:boolean;
  status: "unknown" | "authenticating" | "authenticated" | "unauthenticated";
  authenticated: boolean;
  ownerId: OwnerId;
  authorId?: ArtistId;
  menuEnabled: boolean;
  /** True once we've checked the artist doc and confirmed there's no
   *  current-version consent record — meaning we need to prompt. */
  needsConsent: boolean;
  observeAuthentication: (
    onAuthenticated: (artistId: ArtistId, ownerId: string) => void,
    onUnAuthed: (artistId: ArtistId) => void
  ) => void;
  setDarkMode: (isOn: boolean) => void;
  setCompactMode: (isOn: boolean) => void;
  changeProfile: (item: string, value: string) => void;
  changeUserPlaylistOrder: (item: string, value: PlaylistOrder[]) => void;
  /** Persist the user's first-login privacy choice + agreement. Updates
   *  both the consent record and the privacy field in one write. */
  recordConsent: (privacyChoice: "public" | "member") => Promise<void>;
};

export const useProfile = create<ProfileState>((set, get) => ({
  menuEnabled: true,
  status: "unknown",
  darkMode: false,
  compact: false,
  authenticated: false,
  ownerId: "",
  needsConsent: false,
  recordConsent: async (privacyChoice) => {
    const { authorId, ownerId } = get();
    if (!authorId) return;
    const consent: PrivacyConsent = {
      policyVersion: CURRENT_PRIVACY_POLICY_VERSION,
      privacyChoice,
      agreedToTerms: true,
      consentedAt: Date.now(),
    };
    await ProfileService.changeProfile(
      { ownerId, consent, privacy: privacyChoice },
      authorId,
    );
    set({ needsConsent: false });
  },
  changeProfile: (item, value: string) => {
    const { authorId, ownerId } = get();
    let change = { ownerId } as any;
    change[item] = value;
    authorId && ProfileService.changeProfile(change, authorId);
  },
  changeUserPlaylistOrder: (fieldName: string, value: PlaylistOrder[]) => {
    const { authorId, ownerId } = get();
    let change = { ownerId } as any;
    change.playlistsOrder = { [fieldName]: value };

    authorId && ProfileService.changeProfile(change, authorId);
  },
  setDarkMode: (isOn) => {
    const { ownerId } = get();
    set({ darkMode: isOn });
    const darkmodeSetting = { theme: isOn ? "dark" : "light" };
    ownerId && SettingsService.changeSetting(darkmodeSetting, ownerId);
  },
  setCompactMode: (isOn) => {
    const { ownerId } = get();
    set({ compact: isOn });
    const compactModeSetting = { compact: isOn ? "compact" : "default" };
    ownerId && SettingsService.changeSetting(compactModeSetting, ownerId);
  },

  observeAuthentication: (onAuthenticated, onUnAuthed) => {
    const authenticate = async (authorId: ArtistId, ownerId: string) => {
      console.log("authenticated",authorId,ownerId);
      const profile = get();
      const authenticatedProfile = produce(profile, (draftState) => {
        draftState.authenticated = true;
        draftState.authorId = authorId;
        draftState.ownerId = ownerId;
        draftState.status = "authenticated";
      });
      set(authenticatedProfile);
      onAuthenticated(authorId, ownerId);
      const settings = await SettingsService.ArtistSettings(ownerId);
      if (settings.theme === "dark") {
        set({ darkMode: true });
      } else {
        set({ darkMode: false });
      }
      if (settings.compact === "compact") {
        set({ compact: true });
      } else {
        set({ compact: false });
      }

      // FERPA / privacy consent check: if this user has no consent record
      // (or it's for an older policy version), flag for the first-login
      // privacy modal to render. Failure to read the artist doc is treated
      // as "needs consent" — fail safe toward asking.
      try {
        const artist = await ArtistService.fetch(authorId);
        const consent = artist && (artist as any).consent;
        const upToDate =
          consent && consent.policyVersion === CURRENT_PRIVACY_POLICY_VERSION;
        set({ needsConsent: !upToDate });
      } catch {
        set({ needsConsent: true });
      }
    };

    const logOut = () => {
      const profile = get();
      const id = get().authorId || "";
      const loggedOutProfile = produce(profile, (draftState) => {
        draftState.authenticated = false;
        draftState.authorId = "";
        draftState.ownerId = "";
        draftState.status = "unauthenticated";
        draftState.needsConsent = false;
      });
      set(loggedOutProfile);
      onUnAuthed(id);
    };

    LoginService.watchAuthState(authenticate, logOut);
  },
}));
